import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'

// evertel
import { APIDataMedia } from '@evertel/types'
import { useService } from '@evertel/di'
import { useDidMountEffect } from '@evertel/hooks'
import { getClosestVersionByHeight } from '@evertel/utils'
import { Row, Checkbox, Image, Text } from '@evertel/web/ui'
import Wrapper from './Wrapper'
import { SchemaMediaController } from '../../controller/medias/SchemaMediaController'

const OPTION_IMAGE_WIDTH = 100
const OPTION_IMAGE_HEIGHT = 66

// request a resized version sized for the option thumbnail (2x for retina) rather
// than the full-size default the server would otherwise serve. Falls back to the
// raw url for media whose versions haven't been generated yet
const optionImageUrl = (media?: APIDataMedia) => {
    if (!media?.url) return undefined
    return getClosestVersionByHeight(OPTION_IMAGE_HEIGHT * 2, media)?.url || media.url
}

export interface CheckboxWriteWidgetProps {
    id: string
    htmlId: string
    value: string|string[]
    onChange?: (id: string|number, value: string|boolean|string[]|undefined) => void
    title: string
    label?: string
    required?: boolean
    hint?: string
    description?: string
    scope?: string
    errors?: string
    allowMultiple?: boolean
    options: {value: string, label: string, image: number|File}[]
    schemaId?: number
}

const CheckboxWriteWidget: React.FC<CheckboxWriteWidgetProps> = observer(({
    id,
    htmlId,
    value,
    onChange,
    title,
    label,
    required,
    options,
    scope,
    errors,
    allowMultiple = true,
    schemaId,
    ...otherProps
}) => {

    const fieldMediaController = useService(SchemaMediaController, [schemaId])

    const [media, setMedia] = useState<APIDataMedia[]>([])
    const [values, setValues] = useState(Array.isArray(value) ? value : (value != null ? [value] : []))

    const inputId = `input-${htmlId}`
    const labelId = `label-${htmlId}`
    const descriptionId = `description-${htmlId}`
    const errorId = `error-${htmlId}`
    const hasErrors = errors && errors.length > 0

    const mediaIds = options?.filter(o => o.image && typeof o.image === 'number').map(o => o.image as number) || []
    const mediaIdsKey = mediaIds.join(',')

    useEffect(() => {
        (async () => {
            await fieldMediaController.init(schemaId, mediaIds)
            setMedia(fieldMediaController.mediaArrays?.uploaded || [])
        })()
    }, [schemaId, mediaIdsKey])

    useDidMountEffect(() => {
        onChange && onChange(id, values)
    }, [values])

    const _onChange = (e: any) => {
        let _value = values

        if (allowMultiple) {
            if (e.checked && !_value?.includes(e.value)) {
                _value.push(e.value as string)
            } else {
                _value = _value?.filter(v => v !== e.value)
            }
        } else {
            _value = (e.checked) ? [e.value] : []
        }

        setValues([..._value])
    }


    return (
        <Wrapper
            title={title}
            required={required}
            errors={errors}
            labelId={labelId}
            descriptionId={descriptionId}
            errorId={errorId}
            {...otherProps}
        >
            {(!options?.length) &&
                // backwards compatibility (some original schemas formatted single checkboxes differently)
                <Checkbox
                    id={inputId}
                    name={id}
                    value={value}
                    checked={!!value}
                    onChange={(e) => onChange && onChange(id, e.checked)}
                    label={label || '\u00A0\u00A0\u00A0'}
                    aria-labelledby={title || otherProps.hint ? labelId : undefined}
                    aria-invalid={hasErrors ? true : undefined}
                    aria-errormessage={hasErrors ? errorId : undefined}
                    aria-required={required ? true : undefined}
                    disabled={scope === 'schemaBuilder'}
                />
            }
            {(options?.length > 0) &&
                <fieldset
                    id={inputId}
                    aria-invalid={hasErrors ? true : undefined}
                    aria-errormessage={hasErrors ? errorId : undefined}
                    aria-required={required ? true : undefined}
                >
                    <Row className="flex-wrap">
                        {options?.map((option, idx) => {
                            if (!option) return undefined

                            const imageURL = (option.image instanceof File) ?
                                fieldMediaController?.getLocalFileURL(option.image as File) as string :
                                optionImageUrl(media?.find(m => m.id === Number(option.image))) as string

                            const optionId = `${inputId}-option-${idx}`

                            return (
                                <div key={idx} className="me-3" >
                                    <label>
                                        <Checkbox
                                            id={optionId}
                                            name={option.value}
                                            value={option.value}
                                            checked={values?.includes(option.value)}
                                            onChange={_onChange}
                                            label={option.label}
                                            disabled={scope === 'schemaBuilder'}
                                        />
                                        {(option.image && imageURL) &&
                                        <div className="mb-3">
                                            <Image
                                                url={imageURL}
                                                resizeMode="contain"
                                                alt={`Image for ${option.label}`}
                                                style={{
                                                    width: OPTION_IMAGE_WIDTH,
                                                    height: OPTION_IMAGE_HEIGHT,
                                                    cursor: 'pointer'
                                                }}
                                            />
                                        </div>
                                        }
                                    </label>
                                </div>
                            )
                        })}
                    </Row>
                </fieldset>
            }
        </Wrapper>
    )
})

// ----------------------------------------------------------------------------------------

export interface CheckboxReadWidgetProps {
    id: string
    htmlId: string
    value?: string[]
    title?: string
    label?: string
    schemaId?: number
    documentId?: number
    options: {value: string, label: string, image?: number|string}[]
}

const CheckboxReadWidget: React.FC<CheckboxReadWidgetProps> = observer(({
    id,
    htmlId,
    value = [],
    title,
    options,
    schemaId,
    label,
    ...otherProps
}) => {

    const fieldMediaController = useService(SchemaMediaController, [schemaId])

    const [media, setMedia] = useState<APIDataMedia[]>([])

    const inputId = `input-${htmlId}`
    const labelId = `label-${htmlId}`
    const descriptionId = `description-${htmlId}`

    const values = Array.isArray(value) ? value : (value != null ? [value] : [])

    useEffect(() => {
        (async () => {
            const mediaIds: number[] = []
            options?.forEach(o => {
                if (o.image && typeof o.image === 'number') {
                    mediaIds.push(o.image)
                }
            })

            await fieldMediaController.init(schemaId, mediaIds)
            setMedia(fieldMediaController.mediaArrays?.uploaded || [])
        })()
    }, [schemaId])

    return (
        <Wrapper
            value={value}
            title={title}
            labelId={labelId}
            descriptionId={descriptionId}
            {...otherProps}
        >
            {(!values?.length) && (
                <Text id={labelId}>
                    <>
                        <span className="visually-hidden">None Selected</span>
                        <span aria-hidden="true">---</span>
                    </>
                </Text>
            )}
            {(!options?.length) && (
                <Checkbox
                    id={inputId}
                    name={id}
                    label={label || '\u00A0\u00A0\u00A0'}
                    checked={!!value}
                />
            )}
            {(options?.length > 0) && (
                <fieldset id={inputId}>
                    <Row className="flex-wrap">
                        {options?.map((option, idx) => {
                            if (!option) return undefined

                            const isChecked = values.includes(option.value)
                            const image = option.image ? media?.find(m => m.id === Number(option.image)) : undefined

                            // Skip rendering options that aren't checked
                            if (!isChecked) return null

                            return (
                                <div key={idx} className="me-3" >
                                    <Checkbox
                                        key={idx}
                                        checked={true}
                                        label={option.label}
                                    />
                                    {(option.image && image) &&
                                    <div className="mb-4">
                                        <Image
                                            url={optionImageUrl(image) as string}
                                            resizeMode="contain"
                                            style={{
                                                width: OPTION_IMAGE_WIDTH,
                                                height: OPTION_IMAGE_HEIGHT
                                            }}
                                        />
                                    </div>
                                    }
                                </div>
                            )
                        }).filter(Boolean)}
                    </Row>
                </fieldset>
            )}
        </Wrapper>
    )
})

export {
    CheckboxWriteWidget,
    CheckboxReadWidget
}
